/**
 * Runtime Configuration Loader & Context
 *
 * Fetches environment config from GET /api/config at startup.
 * Stores it on window.ZEN_CONFIG for non-React access (e.g. raw fetch)
 * and exposes a React context for component-level access.
 */
import React, { createContext, useContext, useState, useEffect } from 'react';

// ---------------------------------------------------------------------------
// 1. Bootstrap loader  – call once before React renders
// ---------------------------------------------------------------------------

let _config = null;

export async function loadRuntimeConfig(portal = 'consumer') {
  if (_config) return _config;

  try {
    const res = await fetch(`/api/config?portal=${portal}`);
    if (!res.ok) throw new Error(`Config fetch failed: ${res.status}`);
    _config = await res.json();
  } catch (err) {
    console.warn('[ZEN_CONFIG] Runtime config unavailable, using fallbacks', err);
    _config = {
      environment: process.env.REACT_APP_ENV || 'dev',
      portal: portal,
      app_version: '0.0.0',
      api_base: '/api',
      cdn_base: '',
      media_base: '',
      razorpay_key: '',
      google_places_key: process.env.REACT_APP_GOOGLE_PLACES_API_KEY || '',
      turnstile_site_key: process.env.REACT_APP_TURNSTILE_SITE_KEY || '',
      feature_flags: {},
    };
  }

  window.ZEN_CONFIG = _config;
  return _config;
}

// Quick synchronous accessor (available after loadRuntimeConfig resolves)
export function getConfig() {
  return window.ZEN_CONFIG || _config || {};
}

// ---------------------------------------------------------------------------
// 2. React Context
// ---------------------------------------------------------------------------

const ConfigContext = createContext(null);

export function ConfigProvider({ children }) {
  const [config, setConfig] = useState(window.ZEN_CONFIG || null);

  useEffect(() => {
    if (!config) {
      loadRuntimeConfig().then(setConfig);
    }
  }, [config]);

  if (!config) {
    // Minimal loading state while config loads
    return null;
  }

  return (
    <ConfigContext.Provider value={config}>
      {children}
    </ConfigContext.Provider>
  );
}

export function useConfig() {
  const ctx = useContext(ConfigContext);
  if (!ctx) {
    // Graceful fallback if used outside provider (shouldn't happen)
    return getConfig();
  }
  return ctx;
}

// ---------------------------------------------------------------------------
// 3. Feature flag helper
// ---------------------------------------------------------------------------

export function isFeatureEnabled(flagName) {
  const cfg = getConfig();
  return !!(cfg.feature_flags && cfg.feature_flags[flagName]);
}
